import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import Container from "@/components/ui/container";
import Breadcrumbs from "@/components/ui/breadcrumbs";
import SEO from "@/components/SEO";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import WhatsAppFloat from "@/components/ui/whatsapp-float";
import { FileText, AlertCircle, CheckCircle, Scale, Users, Building2 } from "lucide-react";

const Terms = () => {
  const sections = [
    {
      icon: <FileText className="w-6 h-6" />,
      title: "Definições e Escopo",
      content: [
        "Estes termos se aplicam ao uso do site futurestation.com.br e aos serviços prestados pela Future Station.",
        "Usuário: qualquer pessoa que acesse ou utilize nosso site e serviços.",
        "Serviços: consultoria, desenvolvimento, implementação e suporte em soluções TOTVS Fluig.",
        "Conteúdo: todas as informações, textos, imagens e materiais disponibilizados no site."
      ]
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Uso do Site",
      content: [
        "O site é destinado a profissionais e empresas interessadas em soluções Fluig.",
        "É proibido usar o site para atividades ilegais ou que violem direitos de terceiros.",
        "Não é permitido copiar, reproduzir ou distribuir conteúdo sem autorização expressa.",
        "O usuário deve fornecer informações verdadeiras em formulários e contatos."
      ]
    },
    {
      icon: <Building2 className="w-6 h-6" />,
      title: "Serviços Prestados",
      content: [
        "Oferecemos consultoria especializada em TOTVS Fluig e desenvolvimento de soluções customizadas.",
        "Todos os serviços são formalizados através de contratos específicos com condições detalhadas.",
        "Cronogramas e entregas são definidos conforme a complexidade de cada projeto.",
        "Garantimos suporte técnico conforme acordado em cada contrato de serviço."
      ]
    },
    {
      icon: <Scale className="w-6 h-6" />,
      title: "Propriedade Intelectual",
      content: [
        "Todo conteúdo do site é protegido por direitos autorais da Future Station.",
        "Soluções desenvolvidas customizadas têm propriedade definida no contrato específico.",
        "FluigApps desenvolvidos pela Future Station mantêm nossa propriedade intelectual.",
        "Respeitamos a propriedade intelectual da TOTVS sobre a plataforma Fluig."
      ]
    },
    {
      icon: <AlertCircle className="w-6 h-6" />,
      title: "Limitações e Responsabilidades",
      content: [
        "Não nos responsabilizamos por problemas decorrentes do uso inadequado de nossas soluções.",
        "A responsabilidade por backup e segurança dos dados do cliente é compartilhada conforme contrato.",
        "Interrupções no site por manutenção ou problemas técnicos podem ocorrer eventualmente.",
        "Não garantimos disponibilidade de 100% do site ou sistemas de terceiros integrados."
      ]
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: "Alterações e Vigência",
      content: [
        "Estes termos podem ser alterados a qualquer momento, com publicação da nova versão no site.",
        "Alterações significativas serão comunicadas aos usuários através de e-mail ou avisos no site.",
        "O uso continuado do site após alterações constitui aceitação dos novos termos.",
        "Em caso de dúvidas sobre os termos, entre em contato conosco para esclarecimentos."
      ]
    }
  ];

  const disclaimer = [
    {
      title: "Sobre a TOTVS Fluig",
      content: "A Future Station é uma empresa especializada independente. Não somos parte da TOTVS, mas parceiros certificados especialistas na plataforma Fluig."
    },
    {
      title: "Jurisdição",
      content: "Estes termos são regidos pelas leis brasileiras. Eventuais disputas serão resolvidas no foro de Olinda/PE."
    },
    {
      title: "Contato Legal",
      content: "Para questões legais ou contratuais: contato@futurestation.com.br ou WhatsApp (81) 98165-8439."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Termos de Uso - Future Station"
        description="Termos e condições para uso do site e contratação de serviços Future Station. Consultoria especializada em TOTVS Fluig com compliance total e transparência."
        keywords={['termos de uso future station', 'condições gerais serviço', 'contrato consultoria fluig', 'termos legais totvs', 'condições comerciais']}
        type="website"
      />
      <Header />
      
      <main className="pt-24">
        {/* Breadcrumbs */}
        <Container className="py-6">
          <div className="max-w-6xl mx-auto">
            <Breadcrumbs />
          </div>
        </Container>
        
        {/* Hero Section */}
        <Container className="py-20">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-fs-cyan-500/20 rounded-full">
                <Scale className="w-12 h-12 text-fs-cyan-500" />
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 fs-gradient-text">
              Termos de Uso
            </h1>
            <p className="text-xl text-muted-foreground dark:text-white/70 max-w-3xl mx-auto mb-8">
              Condições gerais para uso do site e contratação dos nossos serviços especializados 
              em TOTVS Fluig. Leia atentamente antes de prosseguir.
            </p>
            <div className="text-sm text-muted-foreground dark:text-white/60">
              Última atualização: Janeiro de 2025
            </div>
          </div>
        </Container>

        {/* Content Sections */}
        <Container className="py-20">
          <div className="max-w-6xl mx-auto">
            <div className="grid gap-8">
              {sections.map((section, index) => (
                <Card key={index} className="bg-card/80 backdrop-blur-sm border-border shadow-md dark:shadow-none hover:border-fs-cyan-500/40 transition-all duration-300">
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-3 text-2xl text-foreground dark:text-white">
                      <div className="text-fs-cyan-500">
                        {section.icon}
                      </div>
                      <span>{section.title}</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {section.content.map((item, itemIndex) => (
                        <p key={itemIndex} className="text-muted-foreground dark:text-white/70 leading-relaxed">
                          • {item}
                        </p>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </Container>

        {/* Disclaimer Section */}
        <Container className="py-20">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12 fs-gradient-text">
              Informações Importantes
            </h2>
            <div className="grid gap-6">
              {disclaimer.map((item, index) => (
                <Card key={index} className="bg-gradient-to-br from-fs-cyan-500/10 to-fs-purple-500/10 border-fs-cyan-500/20">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-bold text-foreground dark:text-white mb-3 flex items-center">
                      <AlertCircle className="w-5 h-5 mr-2 text-fs-cyan-500" />
                      {item.title}
                    </h3>
                    <p className="text-muted-foreground dark:text-white/70 leading-relaxed">
                      {item.content}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </Container>

        {/* Agreement Section */}
        <Container className="py-20">
          <div className="max-w-4xl mx-auto">
            <Card className="bg-gradient-to-br from-fs-purple-500/10 to-fs-cyan-500/10 border-fs-purple-500/20">
              <CardContent className="p-8 text-center">
                <CheckCircle className="w-16 h-16 text-fs-purple-500 mx-auto mb-6" />
                <h2 className="text-2xl font-bold text-foreground dark:text-white mb-4">
                  Aceite dos Termos
                </h2>
                <p className="text-muted-foreground dark:text-white/70 mb-6 leading-relaxed">
                  Ao utilizar nosso site ou contratar nossos serviços, você declara ter lido, 
                  compreendido e concordado com todos os termos e condições aqui estabelecidos.
                </p>
                <p className="text-sm text-white/60">
                  Future Station - Especialistas em TOTVS Fluig<br/>
                  Olinda - Pernambuco | Growth Market - Leonardo Manga<br/>
                  contato@futurestation.com.br | (81) 98165-8439
                </p>
              </CardContent>
            </Card>
          </div>
        </Container>
      </main>
      
      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

export default Terms;